import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as Colors from 'ui-library/Colors';

import {LABEL_PROPS, LABEL_STYLES, LabelProps, LabelStyle} from './InputConstants';

export interface FieldLabelProps extends LabelProps {
  className?: string;
  /**
   * htmlFor can be used in combination with an input ID, which improves accessibility
   * and UX by making it so clicking the label focuses the input.
   */
  htmlFor?: string;
  grayOutForeground?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export function FieldRequiredIndicator(): JSX.Element {
  return <span className="u-danger-color">*</span>;
}

/**
 * Component for labels, providing styling and required asterisk.
 * A hidden label style is provided for accessibility.
 *
 * Prefer this label over `ui-library/bs/Label` for greenfield usage.
 */
export default class FieldLabel extends React.Component<FieldLabelProps> {
  static propTypes = {
    ...LABEL_PROPS,
    className: PropTypes.string,
    htmlFor: PropTypes.string,
    grayOutForeground: PropTypes.bool,
  };

  static defaultProps = {
    isRequired: false,
    labelStyle: LabelStyle.LABEL,
  };

  static STYLES = LABEL_STYLES;

  override render(): JSX.Element {
    return (
      <label
        className={classNames(
          this.props.grayOutForeground && css(styles.grayForeground),
          this.props.className,
          {
            'fieldLabel-label': this.props.labelStyle === LABEL_STYLES.LABEL,
            'fieldLabel-sublabel': this.props.labelStyle === LABEL_STYLES.SUBLABEL,
            'sr-only': this.props.labelStyle === LABEL_STYLES.HIDDEN,
          }
        )}
        htmlFor={this.props.htmlFor}
        {...getTestProps(this.props.dataTestAttrs)}
      >
        {this.props.label}
        {this.props.isRequired && <FieldRequiredIndicator />}
      </label>
    );
  }
}

const styles = StyleSheet.create({
  grayForeground: {
    color: Colors.GRAY_DARK,
  },
});
